import React, { useState, useRef } from 'react';
import {
  ArrowUp,
  ArrowDown,
  Trash2,
  Upload,
  Loader2,
  Eraser,
  Lock,
  Unlock,
  Copy,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Switch } from '../../ui/switch';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '../../ui/tooltip';
import { NumberField, ColorField, SelectField, TokenPicker } from '../../canvas-shared/propertyFields';
import { CollapsibleSection } from '../../canvas-shared/CollapsibleSection';
import { FONT_OPTIONS, WEIGHT_OPTIONS } from '../../canvas-shared/canvasConstants';
import { sanitizeImageUrl, resolveTokens } from '../../canvas-shared/canvasUtils';
import { feedWatchService } from '../../../services/feedWatch';
import { jobService } from '../../../services/job';
import type { CanvasElement, CanvasElementType } from '../../../services/feedWatch';

// ── Props ────────────────────────────────────────────────────

interface PropertyPanelProps {
  element: CanvasElement;
  detectedFields: string[];
  sampleData?: Record<string, string>;
  onUpdate: (updates: Partial<CanvasElement>) => void;
  onDelete: () => void;
  onMoveLayer: (direction: 'up' | 'down') => void;
  onDuplicate?: () => void;
}

const TYPE_LABELS: Record<CanvasElementType, string> = {
  text: 'Text',
  image: 'Image',
  rect: 'Rectangle',
  circle: 'Circle',
};

// ── Main Component ───────────────────────────────────────────

export function PropertyPanel({
  element,
  detectedFields,
  sampleData,
  onUpdate,
  onDelete,
  onMoveLayer,
  onDuplicate,
}: PropertyPanelProps) {
  return (
    <TooltipProvider delayDuration={300}>
      <div className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold">{TYPE_LABELS[element.type]}</h3>
            {element.name && (
              <p className="text-[10px] text-muted-foreground truncate max-w-[120px]">{element.name}</p>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`h-7 w-7 p-0 ${element.locked ? 'text-primary' : ''}`}
                  onClick={() => onUpdate({ locked: !element.locked })}
                >
                  {element.locked ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{element.locked ? 'Unlock' : 'Lock'}</TooltipContent>
            </Tooltip>
            {onDuplicate && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onDuplicate}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Duplicate (Ctrl+D)</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onMoveLayer('up')}>
                  <ArrowUp className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Move Up</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onMoveLayer('down')}>
                  <ArrowDown className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Move Down</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                  onClick={onDelete}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Delete (Del)</TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* Name */}
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Name</label>
          <Input
            value={element.name || ''}
            onChange={(e) => onUpdate({ name: e.target.value } as Partial<CanvasElement>)}
            placeholder="Element name"
            className="h-8 text-sm rounded-lg"
          />
        </div>

        {/* Position & Size */}
        <CollapsibleSection title="Position & Size">
          <div className="grid grid-cols-2 gap-2">
            <NumberField label="X" value={element.x} onChange={(v) => onUpdate({ x: v })} />
            <NumberField label="Y" value={element.y} onChange={(v) => onUpdate({ y: v })} />
            <NumberField label="Width" value={element.width} onChange={(v) => onUpdate({ width: v })} />
            <NumberField
              label="Height"
              value={element.height}
              onChange={(v) => onUpdate({ height: v })}
              disabled={element.type === 'circle'}
            />
            <NumberField
              label="Rotation"
              value={element.rotation ?? 0}
              onChange={(v) => onUpdate({ rotation: v })}
            />
          </div>
        </CollapsibleSection>

        {/* Appearance */}
        <CollapsibleSection title="Appearance">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-muted-foreground">Opacity</label>
              <span className="text-xs text-muted-foreground tabular-nums">
                {Math.round((element.opacity ?? 1) * 100)}%
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={element.opacity ?? 1}
              onChange={(e) => onUpdate({ opacity: Number(e.target.value) } as Partial<CanvasElement>)}
              className="w-full h-2 bg-muted rounded-full accent-primary cursor-pointer"
            />

            {/* Shadow toggle */}
            <ShadowControls element={element} onUpdate={onUpdate} />
          </div>
        </CollapsibleSection>

        {/* Type-specific */}
        <CollapsibleSection title="Properties">
          {element.type === 'text' && (
            <TextProperties
              element={element}
              detectedFields={detectedFields}
              sampleData={sampleData}
              onUpdate={onUpdate}
            />
          )}
          {element.type === 'image' && (
            <ImageProperties
              element={element}
              detectedFields={detectedFields}
              sampleData={sampleData}
              onUpdate={onUpdate}
            />
          )}
          {element.type === 'rect' && <RectProperties element={element} onUpdate={onUpdate} />}
          {element.type === 'circle' && <CircleProperties element={element} onUpdate={onUpdate} />}
        </CollapsibleSection>
      </div>
    </TooltipProvider>
  );
}

// ── Shadow Controls ──────────────────────────────────────────

function ShadowControls({
  element,
  onUpdate,
}: {
  element: CanvasElement;
  onUpdate: (u: Partial<CanvasElement>) => void;
}) {
  const hasShadow = !!element.shadow;
  const shadow = element.shadow || { x: 2, y: 2, blur: 4, color: 'rgba(0,0,0,0.25)' };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-muted-foreground">Drop Shadow</label>
        <Switch
          checked={hasShadow}
          onCheckedChange={(checked) => {
            if (checked) {
              onUpdate({ shadow: { x: 2, y: 2, blur: 4, color: 'rgba(0,0,0,0.25)' } } as Partial<CanvasElement>);
            } else {
              onUpdate({ shadow: undefined } as Partial<CanvasElement>);
            }
          }}
        />
      </div>
      {hasShadow && (
        <div className="grid grid-cols-2 gap-2 pl-2">
          <NumberField label="X" value={shadow.x} onChange={(v) => onUpdate({ shadow: { ...shadow, x: v } } as Partial<CanvasElement>)} />
          <NumberField label="Y" value={shadow.y} onChange={(v) => onUpdate({ shadow: { ...shadow, y: v } } as Partial<CanvasElement>)} />
          <NumberField label="Blur" value={shadow.blur} onChange={(v) => onUpdate({ shadow: { ...shadow, blur: v } } as Partial<CanvasElement>)} />
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Color</label>
            <Input
              value={shadow.color}
              onChange={(e) => onUpdate({ shadow: { ...shadow, color: e.target.value } } as Partial<CanvasElement>)}
              className="h-8 text-sm rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Text Properties ──────────────────────────────────────────

function TextProperties({
  element,
  detectedFields,
  sampleData,
  onUpdate,
}: {
  element: CanvasElement & { type: 'text' };
  detectedFields: string[];
  sampleData?: Record<string, string>;
  onUpdate: (u: Partial<CanvasElement>) => void;
}) {
  // Token preview — show resolved text
  const resolvedPreview = sampleData
    ? resolveTokens(element.content, sampleData)
    : null;
  const hasTokens = element.content.includes('{{');

  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground">Content</label>
        <textarea
          value={element.content}
          onChange={(e) => onUpdate({ content: e.target.value } as Partial<CanvasElement>)}
          rows={3}
          className="flex w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
        />
      </div>

      {/* Token preview */}
      {hasTokens && resolvedPreview && (
        <div className="px-2 py-1.5 rounded-lg bg-muted/50 border border-dashed border-muted-foreground/20">
          <p className="text-[10px] text-muted-foreground mb-0.5">Preview:</p>
          <p className="text-xs truncate">{resolvedPreview}</p>
        </div>
      )}

      <TokenPicker
        fields={detectedFields}
        sampleData={sampleData}
        onInsert={(token) => onUpdate({ content: element.content + token } as Partial<CanvasElement>)}
      />

      {/* Render HTML toggle */}
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-muted-foreground">Render HTML</label>
        <Switch
          checked={element.renderHtml ?? false}
          onCheckedChange={(checked) => onUpdate({ renderHtml: checked } as Partial<CanvasElement>)}
        />
      </div>
      {element.renderHtml && (
        <p className="text-[10px] text-muted-foreground">
          HTML tags will be rendered as formatted text.
        </p>
      )}

      {/* Max Characters */}
      <NumberField
        label="Max Characters"
        value={element.maxChars ?? 0}
        onChange={(v) => onUpdate({ maxChars: v } as Partial<CanvasElement>)}
        help="0 = no limit. Truncates text at this character count."
      />

      <NumberField
        label="Font Size"
        value={element.fontSize}
        onChange={(v) => onUpdate({ fontSize: v } as Partial<CanvasElement>)}
      />
      <SelectField
        label="Font Weight"
        value={element.fontWeight}
        options={WEIGHT_OPTIONS}
        onChange={(v) => onUpdate({ fontWeight: v } as Partial<CanvasElement>)}
      />
      <SelectField
        label="Font Family"
        value={element.fontFamily}
        options={FONT_OPTIONS}
        onChange={(v) => onUpdate({ fontFamily: v } as Partial<CanvasElement>)}
      />
      <ColorField
        label="Color"
        value={element.color}
        onChange={(v) => onUpdate({ color: v } as Partial<CanvasElement>)}
      />
      <SelectField
        label="Text Align"
        value={element.textAlign}
        options={[
          { value: 'left', label: 'Left' },
          { value: 'center', label: 'Center' },
          { value: 'right', label: 'Right' },
        ]}
        onChange={(v) => onUpdate({ textAlign: v } as Partial<CanvasElement>)}
      />
    </div>
  );
}

// ── Image Properties ─────────────────────────────────────────

function ImageProperties({
  element,
  detectedFields,
  sampleData,
  onUpdate,
}: {
  element: CanvasElement & { type: 'image' };
  detectedFields: string[];
  sampleData?: Record<string, string>;
  onUpdate: (u: Partial<CanvasElement>) => void;
}) {
  const [isUploading, setIsUploading] = useState(false);
  const [isRemovingBg, setIsRemovingBg] = useState(false);
  const [bgError, setBgError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setIsUploading(true);
      const result = await feedWatchService.uploadCanvasAsset(file);
      onUpdate({ src: result.url } as Partial<CanvasElement>);
    } catch {
      // handled
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground">Image Source</label>
        <Input
          value={element.src}
          onChange={(e) => onUpdate({ src: e.target.value } as Partial<CanvasElement>)}
          placeholder="URL or {{field}}"
          className="h-8 text-sm rounded-lg"
        />
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          className="rounded-lg flex-1 text-xs"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
        >
          {isUploading ? (
            <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
          ) : (
            <Upload className="h-3.5 w-3.5 mr-1.5" />
          )}
          {isUploading ? 'Uploading...' : 'Upload Image'}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
          className="hidden"
          onChange={handleUpload}
        />
      </div>
      <TokenPicker
        fields={detectedFields}
        sampleData={sampleData}
        onInsert={(token) => onUpdate({ src: token } as Partial<CanvasElement>)}
      />

      {/* Remove Background */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            {isRemovingBg ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            ) : (
              <Eraser className="h-3.5 w-3.5 text-muted-foreground" />
            )}
            <label className="text-xs font-medium text-muted-foreground">
              {isRemovingBg ? 'Removing...' : 'Remove Background'}
            </label>
          </div>
          <Switch
            checked={element.removeBg ?? false}
            disabled={!element.src || isRemovingBg}
            onCheckedChange={async (checked) => {
              setBgError(null);
              if (!checked) {
                onUpdate({ removeBg: false } as Partial<CanvasElement>);
                return;
              }
              const isToken = element.src.includes('{{');
              const rawResolved = isToken
                ? element.src.replace(/\{\{([^}]+)\}\}/g, (_, key: string) => sampleData?.[key] ?? '')
                : element.src;
              const resolvedSrc = sanitizeImageUrl(rawResolved);

              if (element.bgRemovedSrc && !isToken) {
                onUpdate({ removeBg: true } as Partial<CanvasElement>);
                return;
              }
              if (!resolvedSrc) {
                setBgError('No image URL found. Make sure the image field has a value.');
                return;
              }
              try {
                setIsRemovingBg(true);
                const result = await jobService.removeBackground(resolvedSrc);
                onUpdate({ removeBg: true, bgRemovedSrc: result.url } as Partial<CanvasElement>);
              } catch (err) {
                const msg = err instanceof Error ? err.message : String(err);
                setBgError(msg || 'Background removal failed');
              } finally {
                setIsRemovingBg(false);
              }
            }}
          />
        </div>
        {bgError && (
          <p className="text-xs text-destructive px-1">{bgError}</p>
        )}
        {element.removeBg && !bgError && (
          <p className="text-[10px] text-muted-foreground">
            Background will be removed using AI.
          </p>
        )}
      </div>

      <SelectField
        label="Object Fit"
        value={element.objectFit}
        options={[
          { value: 'cover', label: 'Cover' },
          { value: 'contain', label: 'Contain' },
          { value: 'fill', label: 'Fill' },
        ]}
        onChange={(v) => onUpdate({ objectFit: v } as Partial<CanvasElement>)}
        help="Cover fills the area (may crop), Contain fits entirely, Fill stretches"
      />
      <NumberField
        label="Border Radius"
        value={element.borderRadius}
        onChange={(v) => onUpdate({ borderRadius: v } as Partial<CanvasElement>)}
      />
    </div>
  );
}

// ── Rect Properties ──────────────────────────────────────────

function RectProperties({
  element,
  onUpdate,
}: {
  element: CanvasElement & { type: 'rect' };
  onUpdate: (u: Partial<CanvasElement>) => void;
}) {
  return (
    <div className="space-y-2">
      <ColorField
        label="Fill"
        value={element.fill}
        onChange={(v) => onUpdate({ fill: v } as Partial<CanvasElement>)}
      />
      <NumberField
        label="Border Radius"
        value={element.borderRadius}
        onChange={(v) => onUpdate({ borderRadius: v } as Partial<CanvasElement>)}
      />
      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground">Border</label>
        <Input
          value={element.border}
          onChange={(e) => onUpdate({ border: e.target.value } as Partial<CanvasElement>)}
          placeholder="1px solid #000"
          className="h-8 text-sm rounded-lg"
        />
      </div>
    </div>
  );
}

// ── Circle Properties ────────────────────────────────────────

function CircleProperties({
  element,
  onUpdate,
}: {
  element: CanvasElement & { type: 'circle' };
  onUpdate: (u: Partial<CanvasElement>) => void;
}) {
  return (
    <ColorField
      label="Fill"
      value={element.fill}
      onChange={(v) => onUpdate({ fill: v } as Partial<CanvasElement>)}
    />
  );
}
